import React, { useEffect, useState } from "react";
import logoWhite from "../../assets/img/all-images/logo-orbi-white-transparent.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "../../utils/swal";
import { toast } from "../../utils/toast";

const resolveAvatarUrl = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;

  const domain = (process.env.REACT_APP_APP_DOMAIN || "").replace(/\/$/, "");
  if (!domain) return value;

  const path = value.replace(/^\/+/, "").replace(/^storage\//, "");
  return `${domain}/storage/${path}`;
};

const Header = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userAvatar, setUserAvatar] = useState({ image: "", letter: "U" });
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(
    localStorage.getItem("selectedCity"),
  );
  const navigate = useNavigate();

  const handleCitySelect = (city) => {
    setSelectedCity(city.name);
    localStorage.setItem("selectedCity", city.name);
    localStorage.setItem("selectedCityId", city.id);
    window.dispatchEvent(new Event("cityChange"));
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    const syncCityFromStorage = () => {
      setSelectedCity(localStorage.getItem("selectedCity") || "");
    };
    window.addEventListener("cityChange", syncCityFromStorage);
    return () => window.removeEventListener("cityChange", syncCityFromStorage);
  }, []);

  const fetchCities = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/getAllWorkLocations`,
      );

      if (response.data.status) {
        const data = response.data.data;
        setCities(Array.isArray(data) ? data : (data?.data ?? []));
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  useEffect(() => {
    const checkLogin = () => {
      const token = localStorage.getItem("user_token");
      setIsLoggedIn(!!token);

      const rawUser = localStorage.getItem("user_data") || localStorage.getItem("user");
      let parsedUser = {};
      try {
        parsedUser = rawUser ? JSON.parse(rawUser) : {};
      } catch {
        parsedUser = {};
      }

      const fullName = (
        parsedUser?.name ||
        `${parsedUser?.first_name || ""} ${parsedUser?.last_name || ""}`
      ).trim();
      const firstLetter = (fullName?.charAt(0) || "U").toUpperCase();
      const rawImage =
        parsedUser?.image ||
        parsedUser?.profile_image ||
        parsedUser?.avatar ||
        "";
      // Prefer full image_url from API; fall back to resolving relative storage paths.
      const image = rawImage
        ? resolveAvatarUrl(parsedUser?.image_url || rawImage)
        : "";
      setUserAvatar({ image, letter: firstLetter });
    };

    checkLogin();
    window.addEventListener("storage", checkLogin);
    window.addEventListener("loginStateChange", checkLogin);

    return () => {
      window.removeEventListener("storage", checkLogin);
      window.removeEventListener("loginStateChange", checkLogin);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const nav = document.querySelector(".sticky-top");

      if (window.scrollY > 250) {
        nav.classList.add("sticky-nav");
        nav.style.top = "0px";
      } else {
        nav.classList.remove("sticky-nav");
        nav.style.top = "0px";
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = (e) => {
    e.preventDefault();
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to log out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, logout!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("user_token");
        localStorage.removeItem("user_data");
        toast.success("Logged out successfully");
        setIsLoggedIn(false);
        navigate("/");
      }
    });
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light sticky-top py-2 px-0">
        <div className="container">
          <Link to="/" className="navbar-brand d-flex align-items-center">
            <img src={logoWhite} alt="Orbi Logo" />
          </Link>

          <div className="header_right">
            <div className="nav-item dropdown city_dropdown">
              <a
                href="#!"
                className="nav-link dropdown-toggle d-flex align-items-center"
                data-bs-toggle="dropdown"
                role="button"
                aria-expanded="false"
              >
                {selectedCity || "Select Location"}
                <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
              </a>

              <div className="dropdown-menu city_dropdown-menu fade-up">
                <h3>Select Current City</h3>
                <p>Your location suggestions will be based on the city</p>

                <div className="selct-city">
                  {cities.map((city) => (
                    <a
                      key={city.id}
                      href="#!"
                      className={`dropdown-item ${selectedCity === city.name ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleCitySelect(city);
                      }}
                    >
                      {city.name}
                    </a>
                  ))}
                  {cities.length === 0 && (
                    <p className="p-2 text-muted">Loading cities...</p>
                  )}
                </div>
              </div>
            </div>

            {isLoggedIn ? (
              <div className="nav-item dropdown city_dropdown profile_dropdown">
                <a
                  href="#!"
                  className="nav-link dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  {userAvatar.image ? (
                    <img
                      src={userAvatar.image}
                      alt="User"
                      onError={() =>
                        setUserAvatar((prev) => ({ ...prev, image: "" }))
                      }
                    />
                  ) : (
                    <span
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        background: "#f8ba05",
                        color: "#111",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {userAvatar.letter}
                    </span>
                  )}
                </a>
                <div className="dropdown-menu city_dropdown-menu profile_dropdown-menu fade-up">
                  <Link to="/profile" className="dropdown-item">
                    My Profile
                  </Link>
                  <Link to="/bookings" className="dropdown-item">
                    My Bookings
                  </Link>
                  <a href="#!" className="dropdown-item" onClick={handleLogout}>
                    Logout
                  </a>
                </div>
              </div>
            ) : (
              <Link to="/login" className="nav-item nav-link partner_with">
                Log In
              </Link>
            )}
          </div>
        </div>
      </nav>
     
    </>
  );
};

export default Header;